/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `eventApi` for the Caintra Connect frontend.
 * @module utils/eventApi
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "./logger.js";
import { getToken, refreshToken, signOut } from "./common.js";
import {
  SUCCESS_TOAST_ANIMATION_MS,
  SUCCESS_TOAST_VISIBLE_MS,
} from "./eventUiConstants.js";

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "./apiConstants.js";

export {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
};

/** HTTP 409 Conflict (e.g. already registered). */
export const HTTP_CONFLICT = 409;

/** HTTP 400 Bad Request. */
export const HTTP_BAD_REQUEST = 400;

/** HTTP 404 Not Found. */
export const HTTP_NOT_FOUND = 404;

/** HTTP 401 Unauthorized. */
export const HTTP_UNAUTHORIZED = 401;

/** HTTP 403 Forbidden (valid JWT, role or resource denied). */
export const HTTP_FORBIDDEN = 403;

/** HTTP 422 Unprocessable Entity. */
export const HTTP_UNPROCESSABLE_ENTITY = 422;

/** Capture group index for base64 payload in {@link BASE64_DATA_URL_PATTERN}. */
export const BASE64_PAYLOAD_CAPTURE_INDEX = 2;

/** Audience: members only (`audiencia` = 0). */
export const AUDIENCIA_MEMBERS_ONLY = 0;

/** Audience: non-members only (`audiencia` = 1). */
export const AUDIENCIA_NON_MEMBERS_ONLY = 1;

/** Audience: both member tiers (`audiencia` = 2). */
export const AUDIENCIA_BOTH = 2;

/** Matches base64 data URLs for S3 presigned banner uploads. */
export const BASE64_DATA_URL_PATTERN = /^data:([A-Za-z-+/]+);base64,(.+)$/;

/**
 * Maps API `audiencia` to form state: `0` members only, `1` non-members only, `2` both, else `null`.
 * @param {string|number|null|undefined} value
 * @returns {0|1|2|null}
 */
export const normalizeAudienciaFromApi = (value) => {
  if (value === AUDIENCIA_MEMBERS_ONLY || value === "0") {
    return AUDIENCIA_MEMBERS_ONLY;
  }
  if (value === AUDIENCIA_NON_MEMBERS_ONLY || value === "1") {
    return AUDIENCIA_NON_MEMBERS_ONLY;
  }
  if (value === AUDIENCIA_BOTH || value === "2") {
    return AUDIENCIA_BOTH;
  }
  return null;
};

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export const isTokenExpiredResponse = (data) =>
  data?.message === TOKEN_EXPIRED_MESSAGE ||
  data?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;

/**
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export const isExpiredApiPayload = (data) =>
  data?.message === TOKEN_EXPIRED_MESSAGE;

/**
 * @returns {Record<string, string>}
 */
export const getAuthJsonHeaders = () => {
  const headers = {
    "Content-Type": CONTENT_TYPE_JSON,
  };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/**
 * @param {Response|null|undefined} response
 * @param {object|null|undefined} data
 * @returns {boolean}
 */
export const isForbiddenResponse = (response, data) =>
  response?.status === HTTP_FORBIDDEN || data?.code === "FORBIDDEN";

/**
 * @param {unknown} data
 * @returns {string|null} Presigned HTTPS URL, or null if the body is not a download link
 */
export const parseExportDownloadUrl = (data) => {
  if (typeof data === "string" && /^https?:\/\//i.test(data)) {
    return data;
  }
  if (data && typeof data === "object") {
    for (const key of ["url", "downloadUrl", "s3Url", "link"]) {
      const value = data[key];
      if (typeof value === "string" && /^https?:\/\//i.test(value)) {
        return value;
      }
    }
  }
  return null;
};

/**
 * Throws on 403 / non-expiry 401 so callers do not treat denied JSON as data or a file URL.
 * @param {Response} response
 * @param {object|null|undefined} data
 * @param {Function} [navigate]
 * @returns {Promise<void>}
 */
export const assertAuthorizedApiResponse = async (response, data, navigate) => {
  if (isForbiddenResponse(response, data)) {
    const err = new Error(data?.message || "Forbidden");
    err.code = data?.code || "FORBIDDEN";
    throw err;
  }
  if (response.status === HTTP_UNAUTHORIZED && !isExpiredApiPayload(data)) {
    await signOut();
    if (navigate) {
      navigate("/");
    }
    const err = new Error(data?.message || "Unauthorized");
    err.code = "UNAUTHORIZED";
    throw err;
  }
};

/**
 * Logs to the console only outside production builds.
 * @param {...unknown} args
 */
export const logDevError = (...args) => {
  if (process.env.NODE_ENV !== "prod") {
    logger.error(...args);
  }
};

/**
 * Refreshes the token or signs out. Invokes `retry` when refresh succeeds.
 * @template T
 * @param {import("react-router").NavigateFunction} navigate
 * @param {() => Promise<T>} retry
 * @returns {Promise<{ redirected: true }|{ redirected: false, result: T }>}
 */
export const handleExpiredToken = async (navigate, retry) => {
  const newTokens = await refreshToken();
  if (!newTokens) {
    await signOut();
    navigate("/");
    return { redirected: true };
  }
  return { redirected: false, result: await retry() };
};

/**
 * When `data` is a token-expiry payload, refresh (or sign out) and optionally retry.
 * @template T
 * @param {unknown} data
 * @param {import("react-router").NavigateFunction} navigate
 * @param {() => Promise<T>} retry
 * @returns {Promise<{ redirected: true }|{ redirected: false, result: T }|null>}
 */
export const resolveExpiredToken = async (data, navigate, retry) => {
  if (!isExpiredApiPayload(data)) {
    return null;
  }
  const refresh = await handleExpiredToken(navigate, retry);
  if (refresh.redirected) {
    return { redirected: true };
  }
  return { redirected: false, result: refresh.result };
};

/**
 * Like {@link resolveExpiredToken} but also treats invalid-token messages as expired.
 * @template T
 * @param {unknown} data
 * @param {import("react-router").NavigateFunction} navigate
 * @param {() => Promise<T>} retry
 * @returns {Promise<{ redirected: true }|{ redirected: false, result: T }|null>}
 */
export const resolveAuthExpiredPayload = async (data, navigate, retry) => {
  if (!isTokenExpiredResponse(data)) {
    return null;
  }
  const refresh = await handleExpiredToken(navigate, retry);
  if (refresh.redirected) {
    return { redirected: true };
  }
  return { redirected: false, result: refresh.result };
};

/**
 * Uploads a file or blob to a presigned S3 URL.
 * @param {string} presignedUrl
 * @param {File|Blob|Buffer} file
 * @param {string} [formatoImagen]
 * @returns {Promise<void>}
 */
export const uploadFileToPresignedUrl = async (
  presignedUrl,
  file,
  formatoImagen,
) => {
  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": formatoImagen || file.type || "application/octet-stream",
    },
  });
  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
  }
};

/**
 * Uploads a banner image from a base64 data URL to a presigned S3 URL.
 * @param {string} presignedUrl
 * @param {string} imagenBanner
 * @param {string} [formatoImagen]
 * @param {typeof import("buffer").Buffer} BufferCtor
 * @returns {Promise<void>}
 */
export const uploadEventBannerFromDataUrl = async (
  presignedUrl,
  imagenBanner,
  formatoImagen,
  BufferCtor,
) => {
  const matches = String(imagenBanner).match(BASE64_DATA_URL_PATTERN);
  if (!matches) {
    throw new Error("Invalid banner image data");
  }
  const base64Data = BufferCtor.from(
    matches[BASE64_PAYLOAD_CAPTURE_INDEX],
    "base64",
  );
  await uploadFileToPresignedUrl(presignedUrl, base64Data, formatoImagen);
};

/**
 * Fetches permission payload for the user and updates app permission state.
 * @param {string} userId
 * @param {Function} changePermissions
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<void>}
 */
export const fetchUserRoleData = async (
  userId,
  changePermissions,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();
    if (isTokenExpiredResponse(roleData)) {
      const refresh = await handleExpiredToken(navigate, () =>
        fetchUserRoleData(userId, changePermissions, navigate),
      );
      if (refresh.redirected) {
        return undefined;
      }
      return refresh.result;
    }
    if (response.ok && roleData?.permisos != null) {
      changePermissions(roleData.permisos);
      return roleData.permisos;
    }
    return undefined;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

/**
 * Shows a transient success toast after event creation.
 * @param {Function} setShowSuccessMessage
 * @param {Function} setIsSlidingUp
 * @returns {void}
 */
export const showEventCreatedSuccess = (
  setShowSuccessMessage,
  setIsSlidingUp,
) => {
  setShowSuccessMessage(true);
  setIsSlidingUp(false);

  setTimeout(() => {
    setIsSlidingUp(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
      setIsSlidingUp(false);
    }, SUCCESS_TOAST_ANIMATION_MS);
  }, SUCCESS_TOAST_VISIBLE_MS);
};
